import get from 'lodash/get';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAwaitControl } from 'react-redux-await-control';
import { useTheme } from 'styled-components';
import moment from 'moment';
import { TicketPrioritiesColors } from '~/typings/enums/Ticket/TicketPriorities';

import { Button, Flex, InfoGrid, RenderRichText, Shimmer, Tabs, Tag, Tooltip, Typography } from '~/ui/components';
import { Flag, Pencil, Megaphone } from '~/ui/assets/icons';
import {
  getTicketAccompaniments,
  getTicketComments,
  getTicketDetails,
  getTicketStatus,
  getTicketSupportService,
} from '~/store/tickets/actions';
import useAsyncActionStatus from '~/hooks/useAsyncActionStatus';
import { isSlaOverdue } from '~/screens/Attendance/slaInfo';
import { LOAN_TYPE_NAMES } from '~/typings/enums/LoanType';

import { useDrawer } from '~/hooks/useDrawer';
import { getTicketStatusColor, isTicketEditable, TicketPrioritiesNames } from '~/common/tickets';
import { findLastNameById } from '~/common/selectValues';
import CommentsList from '~/components/CommentsList/CommentsList';
import { getLoggedUser } from '~/store/user/actions';
import { TicketContractsPopover } from '~/components/Ticket/components/TicketContractsPopover/TicketContractsPopover';
import { TicketFiles } from '~/components/Ticket/components/TicketFiles/TicketFiles';
import { getTicketCreationOriginVisual } from '~/components/Ticket/common/ticketCreationOrigin';
import { displayableContractsOf } from '~/components/Ticket/common/ticketContracts';
import { extractAssignedAt, formatElapsed } from '~/components/Ticket/common/assignedTime';
import LogoSmall from '~/ui/assets/imgs/logoSmall.svg';
import { TicketModal } from '~/components';
import History from '../../components/History/History';
import ResolveTicket from '../ResolveTicket';
import TicketDetailsLoading from './TicketDetailsLoading';
import { ContractTab } from './ContractTab';
import ServiceHistory from '../../components/ServiceHistory/ServiceHistory';
import {
  Card,
  Content,
  EditButton,
  OpenTag,
  TagsRow,
  ChannelTag,
  HeaderActions,
  GriContainer,
  InfoContainer,
  PersonLinkWrapper,
  PersonLinkId,
  TopSection,
  TopTabsBar,
  TopTabItem,
  Wrapper,
  SlaInfo,
  OriginBadge,
  DemandBlock,
  DemandTitle,
  DemandText,
  ContractSection,
  ContractItem,
  ContractItemHeader,
  ContractItemInfo,
  ContractInline,
  ContractToggle,
  ContractDetail,
} from './TicketDetails.styles';

interface TicketDetailsProps {
  id: string;
  tab?: string;
  /**
   * Modo embutido (tela de Atendimento): esconde ações/campos que passam a viver
   * no cabeçalho da tela (Person ID, status, botão resolver) e mostra contadores
   * nas abas. Default false — não afeta o uso normal do drawer.
   */
  embedded?: boolean;
}

const NOT_INFORMED = 'Não informado';

function validContractsOf(ticketDetail: any) {
  return displayableContractsOf(ticketDetail?.contracts);
}

// aba Contrato montada sob demanda (fora do modo embutido) — mantém montada e
// alterna a visibilidade para não refazer as buscas ao trocar de aba
function ContractTabSection({
  show,
  visible,
  contracts,
}: Readonly<{ show: boolean; visible: boolean; contracts?: any[] }>) {
  if (!show || !contracts?.length) return null;
  return (
    <div style={{ display: visible ? 'block' : 'none' }}>
      <ContractTab
        contractIds={contracts.map((c) => String(c.id))}
        contracts={contracts.map((c) => ({ id: String(c.id), contractId: c.contractId }))}
      />
    </div>
  );
}

// coluna direita do cabeçalho: status + SLA (variante embutida tem o cronômetro)
function StatusSlaColumn({
  embedded,
  slaText,
  attendanceTimer,
  statusTag,
  dueDate,
}: Readonly<{
  embedded: boolean;
  slaText: string | null;
  attendanceTimer: string | null;
  statusTag: React.ReactNode;
  dueDate?: string;
}>) {
  if (embedded) {
    return (
      <>
        <Flex align="center" justify="flex-end" width="100%" gap={10}>
          {slaText && (
            <SlaInfo>
              <b>{slaText}</b>
            </SlaInfo>
          )}
          {statusTag}
        </Flex>
        {attendanceTimer && (
          <Flex align="center" justify="flex-end" width="100%" gap={10}>
            <SlaInfo>
              Em atendimento: <b>{attendanceTimer}</b>
            </SlaInfo>
          </Flex>
        )}
      </>
    );
  }
  return (
    <>
      <Flex align="center" justify="flex-end" width="100%" gap={10}>
        <Typography type="bodyXSmall">Status do ticket</Typography>
        {statusTag}
      </Flex>
      {!!dueDate && (
        <Flex align="center" justify="flex-end" width="100%" gap={10}>
          <SlaInfo>
            SLA <b>{moment(dueDate).format('DD/MM/YYYY')}</b>
          </SlaInfo>
        </Flex>
      )}
    </>
  );
}

// motivo em destaque + descrição do primeiro atendimento (modo embutido)
function EmbeddedDemand({
  show,
  reason,
  demandDescription,
}: Readonly<{ show: boolean; reason?: string; demandDescription: string | null }>) {
  if (!show || (!reason && !demandDescription)) return null;
  return (
    <DemandBlock>
      {reason && <DemandTitle>{reason}</DemandTitle>}
      {demandDescription && (
        <DemandText>
          <RenderRichText html={demandDescription} />
        </DemandText>
      )}
    </DemandBlock>
  );
}

function TicketDetails({ id, tab, embedded = false }: Readonly<TicketDetailsProps>) {
  const { openDrawer, closeAllDrawers, setConfig } = useDrawer();
  const { colors } = useTheme();

  const getTicketDetailsControl = useAwaitControl(getTicketDetails);
  const getTicketSupportServiceControl = useAwaitControl(getTicketSupportService);
  const ticketStatusControl = useAwaitControl(getTicketStatus);
  const getLoggedUserControl = useAwaitControl(getLoggedUser);
  const getTicketHistoryControl = useAwaitControl(getTicketAccompaniments);

  const ticketStatus = ticketStatusControl.result();
  const loggedUser = getLoggedUserControl.result();

  const ticketDetail = getTicketDetailsControl.result(id);
  const ticketSupportService = getTicketSupportServiceControl.result(id);
  const accompaniments = getTicketHistoryControl.result(id);

  const isLoading = getTicketDetailsControl.isRunning(id);
  const isLoadingSupportService = getTicketSupportServiceControl.isRunning(id);
  const isLoadingHistoryControl = getTicketHistoryControl.isRunning(id);

  const ticketDetailHasFailed = getTicketDetailsControl.hasFailure();

  const disableResolveTicket = useMemo(() => !ticketDetail?.canResolveTicket, [ticketDetail]);
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
  const [filesRefreshKey, setFilesRefreshKey] = useState(0);
  const [activeTopTab, setActiveTopTab] = useState<'ticket' | 'contract'>('ticket');
  const [contractTabMounted, setContractTabMounted] = useState(false);
  // toggle do grid de campos do ticket na seção de contratos (modo embutido)
  const [ticketFieldsOpen, setTicketFieldsOpen] = useState(false);

  const validTicketContracts = validContractsOf(ticketDetail);
  const hasContracts = validTicketContracts.length > 0;

  const disableUpdateTicket = useMemo(
    () => !isTicketEditable(ticketStatus, ticketDetail?.ticketStatusId),
    [ticketStatus, ticketDetail?.ticketStatusId],
  );

  const renderFlag = useCallback((priorityTicket) => {
    if (!priorityTicket) return null;

    const colorName = TicketPrioritiesColors?.[priorityTicket];
    const color = get(colors, colorName) || 'gray';

    return (
      <Flex align="center">
        <Flag width={24} height={24} color={color} />
        <Typography type="bodySmall">{TicketPrioritiesNames?.[priorityTicket]}</Typography>
      </Flex>
    );
  }, []);

  const renderContracts = useCallback((contracts) => {
    const validContracts = displayableContractsOf(contracts);
    if (validContracts.length === 0) return NOT_INFORMED;

    return <TicketContractsPopover contracts={validContracts} />;
  }, []);

  const handleResolveTicket = useCallback(() => {
    const isReclameAqui = ticketDetail?.serviceChannelId === 29;
    openDrawer('resolve-ticket', <ResolveTicket id={id} />, {
      title: 'Resolver ticket',
      closable: true,
      width: isReclameAqui ? 480 : 410,
      sideBySide: true,
      scrollbar: true,
    });
  }, [id, ticketDetail]);

  const handleEditTicketRef = useRef<() => void>();

  const handleEditTicket = useCallback(() => {
    if (!ticketDetail) return;
    openDrawer(
      'ticket-modal',
      <TicketModal
        isUpdating
        ticket={ticketDetail}
        readOnlyFields={[
          'channel',
          'contactOrigin',
          'teamId',
          'ticketStatusId',
          'resolvingAreaId',
          'justificationResolutionId',
          'protocolTwilio',
          'description',
        ]}
      />,
      {
        title: 'Editar ticket',
        width: 410,
        closable: true,
        maskClosable: false,
        sideBySide: true,
        scrollbar: true,
      },
    );
  }, [ticketDetail]);

  handleEditTicketRef.current = handleEditTicket;

  const formatDate = useCallback((dateToFormat) => {
    if (!dateToFormat) return 'Indisponível';
    return moment(dateToFormat).format('DD/MM/YY [às] HH[h]mm');
  }, []);

  const renderTitle = useCallback(
    (id: string) => (
      <>
        Ticket nº <b>{id}</b>
      </>
    ),
    [],
  );

  const handleTabChange = (key: string) => {
    if (key === 'history') {
      getTicketHistoryControl.start({ id }, { actionId: id });
    }

    if (key === 'comments') {
      setCommentsRefreshKey((current) => current + 1);
    }

    if (key === 'files') {
      setFilesRefreshKey((current) => current + 1);
    }
  };

  // contador exibido ao lado do título da aba (só no modo embutido)
  const tabBadge = useCallback(
    (count?: number) =>
      embedded && count != null ? (
        <span
          style={{
            marginLeft: 6,
            padding: '0 6px',
            minWidth: 18,
            height: 18,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 9,
            background: 'rgba(128, 128, 128, 0.15)',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {count}
        </span>
      ) : undefined,
    [embedded],
  );

  const servicesCount = ticketSupportService?.metadata?.recordCount;
  const filesCount = ticketDetail?.ticketFiles?.total;

  // QA TC17 (CA4.8): contador também na aba Comentários — mesmo cache/actionId
  // usado pelo CommentsList, que refaz a busca ao adicionar comentário
  const ticketCommentsStatus = useAsyncActionStatus(getTicketComments, id);
  // o badge da aba já é gateado por `embedded` no tabBadge
  const commentsCount = ticketCommentsStatus.result?.length;

  useEffect(() => {
    if (embedded) ticketCommentsStatus.start(id);
  }, [id, embedded]);

  const tabs = useMemo(
    () => [
      {
        key: 'services',
        title: 'Atendimentos',
        badge: tabBadge(servicesCount),
        noPadding: true,
        component: <ServiceHistory id={id} />,
      },
      {
        key: 'history',
        title: 'Histórico',
        noPadding: true,
        component: <History id={id} accompaniments={accompaniments} isLoading={isLoadingHistoryControl} />,
      },
      {
        key: 'comments',
        title: 'Comentários',
        badge: tabBadge(commentsCount),
        noPadding: true,
        component: (
          <CommentsList ticketId={id} loggedUser={loggedUser} refreshKey={commentsRefreshKey} padded={embedded} />
        ),
      },
      {
        key: 'files',
        title: 'Arquivos',
        badge: tabBadge(filesCount),
        noPadding: true,
        component: <TicketFiles ticketId={id} refreshKey={filesRefreshKey} />,
      },
    ],
    [
      id,
      accompaniments,
      isLoadingHistoryControl,
      loggedUser,
      commentsRefreshKey,
      filesRefreshKey,
      tabBadge,
      servicesCount,
      filesCount,
      commentsCount,
    ],
  );

  const list = useMemo(
    () => [
      {
        name: 'Prioridade',
        value: renderFlag(ticketDetail?.priorityTicket),
      },
      {
        name: 'Data de abertura',
        value: formatDate(ticketDetail?.ticketOpeningDate),
      },
      {
        name: 'Última atualização',
        value: formatDate(ticketDetail?.dateTimeChange),
      },
      // no embutido o motivo vira o título da demanda
      ...(embedded
        ? []
        : [
            {
              name: 'Motivo de abertura',
              value: ticketDetail?.reason,
            },
          ]),
      // no modo embutido os contratos aparecem na seção "Contratos vinculados"
      ...(embedded
        ? []
        : [
            {
              name: 'Contratos',
              value: renderContracts(ticketDetail?.contracts),
            },
          ]),
      {
        name: 'Produto',
        value: LOAN_TYPE_NAMES?.[ticketDetail?.product],
      },
      ...(ticketDetail?.category
        ? [
            {
              name: 'Categoria',
              value: ticketDetail.category.description || '-',
            },
          ]
        : []),
      {
        name: 'Justificativa da resolução',
        value: ticketDetail?.justification,
      },
      {
        name: 'Etapa da jornada',
        value: ticketDetail?.journey,
      },
      {
        name: 'Área resolvedora',
        value: ticketDetail?.resolvingArea || '-',
      },
      // no modo embutido esses contadores aparecem nas abas, não no resumo
      ...(embedded
        ? []
        : [
            {
              name: 'Atendimentos',
              value: isLoadingSupportService ? (
                <Shimmer width={40} height={16} />
              ) : (
                ticketSupportService?.metadata?.recordCount
              ),
            },
            {
              name: 'Arquivos anexados',
              value: ticketDetail?.ticketFiles?.total || 0,
            },
          ]),
    ],
    [ticketDetail, ticketSupportService, isLoadingSupportService, embedded],
  );

  const openedBy = getTicketCreationOriginVisual(ticketDetail?.creationOrigin, colors);

  const statusName = findLastNameById(ticketStatus, ticketDetail?.ticketStatusId) || ticketDetail?.statusTicket;

  // SLA real vem de dueDate/isOverdue (QA CORE-3377) — slaDays é a IDADE do
  // ticket (job diário do mt-ticket), nunca derive o vencimento dele
  const slaText = useMemo(() => {
    if (isSlaOverdue(ticketDetail)) return 'SLA vencido';
    const due = ticketDetail?.dueDate ? moment(ticketDetail.dueDate) : null;
    if (!due?.isValid()) return null;
    return `SLA vence ${due.endOf('day').fromNow()} (${due.format('DD/MM/YYYY')})`;
  }, [ticketDetail]);

  // data/hora do último roteamento — evento "Responsável atribuído/alterado"
  const assignedAt = useMemo(() => extractAssignedAt(accompaniments), [accompaniments]);

  // cronômetro do atendimento: conta os segundos em tela desde o roteamento
  const [timerTick, setTimerTick] = useState(0);

  useEffect(() => {
    if (!embedded || !assignedAt) return undefined;
    const interval = setInterval(() => setTimerTick((current) => current + 1), 1000);
    return () => clearInterval(interval);
  }, [embedded, assignedAt]);

  const attendanceTimer = useMemo(() => (assignedAt ? formatElapsed(assignedAt) : null), [assignedAt, timerTick]);

  // no modo embutido, carrega o histórico no mount para calcular o assignedAt
  useEffect(() => {
    if (embedded) {
      getTicketHistoryControl.start({ id }, { actionId: id });
    }
  }, [id, embedded]);

  // descrição da demanda = descrição do PRIMEIRO atendimento (a do ticket é
  // legado replicado — daily com a Letícia); fallback: descrição do ticket
  const demandDescription = useMemo(() => {
    const records = ticketSupportService?.records;
    if (records?.length) {
      const first = [...records].sort((a, b) => `${a?.createdAt || ''}`.localeCompare(`${b?.createdAt || ''}`))[0];
      if (first?.description) return first.description;
    }
    return ticketDetail?.description || null;
  }, [ticketSupportService, ticketDetail]);

  // campos exibidos ao abrir "Ver detalhes" de um contrato (modo embutido)
  const contractDetailList = useMemo(
    () => [
      { name: 'Data de abertura', value: formatDate(ticketDetail?.ticketOpeningDate) },
      { name: 'Última atualização', value: formatDate(ticketDetail?.dateTimeChange) },
      { name: 'Categoria', value: ticketDetail?.category?.description || '—' },
      { name: 'Justificativa da resolução', value: ticketDetail?.justification || '—' },
      { name: 'Etapa da jornada', value: ticketDetail?.journey || '—' },
      { name: 'Área resolvedora', value: ticketDetail?.resolvingArea || '—' },
    ],
    [ticketDetail, formatDate],
  );

  useEffect(() => {
    setConfig('ticket-details', {
      extraAction: (
        <HeaderActions>
          <Button
            className="header-resolve-btn"
            color="primary"
            variant="contained"
            disabled={disableResolveTicket || isLoading}
            onClick={handleResolveTicket}
            rounded
            size="sm"
          >
            Resolver Ticket
          </Button>
          {!disableUpdateTicket && (
            <EditButton onClick={() => handleEditTicketRef.current?.()} title="Editar ticket">
              <Pencil />
            </EditButton>
          )}
        </HeaderActions>
      ),
    });
  }, [disableUpdateTicket, disableResolveTicket, isLoading, handleResolveTicket]);

  useEffect(() => {
    // compara como string: a API devolve id numérico e o prop pode vir string —
    // com !== estrito a condição nunca fecha e o fetch refaz em loop
    if (String(id) !== String(ticketDetail?.id ?? '')) {
      getTicketDetailsControl.start({ id }, { actionId: id });
    }
  }, [id]);

  useEffect(() => {
    if (ticketDetailHasFailed) {
      closeAllDrawers();
    }
  }, [ticketDetailHasFailed]);

  return (
    <Wrapper>
      {!embedded && hasContracts && (
        <TopTabsBar>
          <div style={{ display: 'flex', flex: 1, alignItems: 'flex-end' }}>
            <TopTabItem type="button" $active={activeTopTab === 'ticket'} onClick={() => setActiveTopTab('ticket')}>
              Detalhes do ticket
            </TopTabItem>
            <TopTabItem
              type="button"
              $active={activeTopTab === 'contract'}
              onClick={() => {
                setActiveTopTab('contract');
                setContractTabMounted(true);
              }}
            >
              Detalhes do contrato
            </TopTabItem>
          </div>
        </TopTabsBar>
      )}

      {activeTopTab === 'ticket' && (
        <>
          <Card>
            {isLoading && <TicketDetailsLoading />}

            {!isLoading && (
              <TopSection>
                <Content>
                  <Flex align="center" gap={8}>
                    {/* badge de origem com tooltip — ícone da origem (GPT/Twilio)
                        quando existe; logo "t." (padrão de produção) como fallback */}
                    {embedded && openedBy?.label?.length ? (
                      <Tooltip content={openedBy.label} small>
                        <OriginBadge>{openedBy.icon || <LogoSmall width={14} height={14} />}</OriginBadge>
                      </Tooltip>
                    ) : null}
                    <Typography type="headingH6">{renderTitle(ticketDetail?.id)}</Typography>
                  </Flex>
                  {!embedded && ticketDetail?.personId && (
                    <PersonLinkWrapper>
                      Person ID
                      <PersonLinkId
                        href={`/backoffice/people/${ticketDetail.personId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {ticketDetail.personId}
                      </PersonLinkId>
                    </PersonLinkWrapper>
                  )}
                </Content>
                <Content end>
                  <Flex direction="column" align="center" justify="flex-end" gap={6}>
                    {/* embutido: SLA + pill de status (sem a label "Status do ticket")
                        e o tempo desde o roteamento para o agente */}
                    <StatusSlaColumn
                      embedded={embedded}
                      slaText={slaText}
                      attendanceTimer={attendanceTimer}
                      dueDate={ticketDetail?.dueDate}
                      statusTag={
                        <Tag {...getTicketStatusColor(ticketStatus, ticketDetail?.ticketStatusId)}>{statusName}</Tag>
                      }
                    />
                  </Flex>
                </Content>
              </TopSection>
            )}

            {/* embutido: motivo em destaque + descrição (a do primeiro atendimento;
                a do ticket é o mesmo texto, legado) — HTML sanitizado */}
            <EmbeddedDemand
              show={embedded && !isLoading}
              reason={ticketDetail?.reason}
              demandDescription={demandDescription}
            />

            {/* tags de origem/canal: no embutido a origem vive no tooltip do badge "t.",
                mas a tag de Reclame Aqui continua visível */}
            {(!embedded && openedBy?.label?.length) || ticketDetail?.serviceChannelId === 29 ? (
              <TagsRow>
                {!embedded && openedBy?.label?.length ? (
                  <OpenTag>
                    {openedBy.icon}
                    <span>{openedBy.label}</span>
                  </OpenTag>
                ) : null}
                {ticketDetail?.serviceChannelId === 29 ? (
                  <ChannelTag>
                    <Megaphone />
                    <span>Reclame Aqui</span>
                  </ChannelTag>
                ) : null}
              </TagsRow>
            ) : null}

            {/* no embutido, os campos do ticket vivem SEMPRE no strip abaixo
                (com ou sem contrato) — padronizado, sem título */}
            {!embedded && (
              <InfoContainer>
                <GriContainer>
                  <InfoGrid
                    loading={isLoading}
                    list={list}
                    shimmerLength={6}
                    grid="32% 32% 32%"
                    responsive={{ 620: '49% 49%', 420: '100%' }}
                  />
                </GriContainer>
              </InfoContainer>
            )}

            {/* strip do contrato + campos do ticket (um só grid, com toggle);
                sem contrato mostra "Não informado" para o agente saber */}
            {embedded && !isLoading && (
              <ContractSection>
                <ContractItem>
                  <ContractItemHeader>
                    {ticketDetail?.priorityTicket && (
                      <Flag
                        width={16}
                        height={16}
                        color={get(colors, TicketPrioritiesColors?.[ticketDetail?.priorityTicket]) || 'gray'}
                      />
                    )}
                    <ContractItemInfo>
                      {/* primeiro contrato + popover com os demais (fundo/consignatária) —
                          mesmo componente do detalhamento de ticket atual */}
                      <ContractInline>
                        <em>Contrato</em>{' '}
                        {hasContracts ? <TicketContractsPopover contracts={validTicketContracts} /> : NOT_INFORMED}
                      </ContractInline>
                      <ContractInline>
                        <em>Produto</em> {LOAN_TYPE_NAMES?.[ticketDetail?.product] || NOT_INFORMED}
                      </ContractInline>
                    </ContractItemInfo>
                    <ContractToggle
                      type="button"
                      $open={ticketFieldsOpen}
                      onClick={() => setTicketFieldsOpen((current) => !current)}
                    >
                      {ticketFieldsOpen ? 'Ocultar detalhes' : 'Ver detalhes'}
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M4 6l4 4 4-4"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </ContractToggle>
                  </ContractItemHeader>

                  {ticketFieldsOpen && (
                    <ContractDetail>
                      <InfoGrid
                        list={contractDetailList}
                        grid="32% 32% 32%"
                        responsive={{ 620: '49% 49%', 420: '100%' }}
                      />
                    </ContractDetail>
                  )}
                </ContractItem>
              </ContractSection>
            )}
          </Card>
          <Tabs tabs={tabs} loading={isLoading} onChange={handleTabChange} initialTab={tab} />
        </>
      )}

      <ContractTabSection
        show={!embedded && contractTabMounted && hasContracts}
        visible={activeTopTab === 'contract'}
        contracts={validTicketContracts}
      />
    </Wrapper>
  );
}

export default TicketDetails;
